import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight, Plus, X } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { EVENT_TYPES } from "@/lib/pulseConstants";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";

/**
 * Mobile What We Create — touch-first horizontal service carousel.
 *
 * Replaces the seven vertically stacked mobile cards with a single
 * horizontally swipeable carousel where each card is shown one at a time
 * with a small peek of the next card as a swipe cue.
 *
 * Interaction model:
 *   - CSS scroll-snap (mandatory, snap-start) — no autoplay, manual swipe only
 *   - Tapping the "+" toggle expands the currently active card IN PLACE.
 *     Only one card may be expanded at a time; expanding another closes
 *     the previous one.
 *   - Swiping to a different card auto-collapses the previously expanded
 *     card (tracked via IntersectionObserver on the scroller).
 *   - The user's horizontal position is preserved — card expansion only
 *     grows the active card vertically; sibling widths are unchanged so
 *     snap points stay stable.
 *   - CTA inside the expanded card scrolls to the existing #pulse-brief
 *     form via the shared handlePulseBriefClick helper.
 *
 * Progress:
 *   - 01 / 07 counter (aria-live=polite)
 *   - Seven dots (real <button>s) with aria-current on active
 *   - Optional prev/next arrow buttons (light chrome) for keyboard users
 *   - A subtle "Swipe to explore services" cue that fades on first
 *     interaction (touch, dot click, arrow click, or Escape).
 *
 * Accessibility:
 *   - Scroller: role="region", aria-roledescription="carousel", aria-label
 *   - Slides: role="group", aria-roledescription="slide",
 *     aria-label="{title} — {n} of 7"
 *   - Plus toggle: real <button> with aria-expanded, aria-controls
 *   - Dots/arrows are real <button>s with descriptive labels
 *   - Escape closes the currently open card
 *   - Reduced-motion honored via Framer Motion global viewport settings
 */
export default function MobileEventTypesCarousel() {
    const scrollerRef = useRef(null);
    const slideRefs = useRef([]);
    const [current, setCurrent] = useState(0);
    const [openIdx, setOpenIdx] = useState(null);
    const [showCue, setShowCue] = useState(true);
    // Prevent snap-anchor from fighting the browser during a click-scroll.
    const suppressAutoCollapseRef = useRef(false);

    const total = EVENT_TYPES.length;

    // Track which slide is centred/visible in the scroller.
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller || typeof IntersectionObserver === "undefined") return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                        const idx = Number(entry.target.getAttribute("data-slide-index"));
                        if (!Number.isNaN(idx)) {
                            setCurrent((prev) => {
                                if (prev !== idx) {
                                    // Swiping to another card collapses the
                                    // previous expanded card. Skip when the
                                    // change is due to a programmatic
                                    // scrollTo triggered from a dot click on
                                    // an expanded card the user just opened.
                                    if (!suppressAutoCollapseRef.current) {
                                        setOpenIdx((cur) => (cur !== null && cur !== idx ? null : cur));
                                    }
                                }
                                return idx;
                            });
                        }
                    }
                });
            },
            { root: scroller, threshold: [0.6, 0.9] }
        );
        slideRefs.current.forEach((el) => { if (el) io.observe(el); });
        return () => io.disconnect();
    }, []);

    // Hide the swipe cue on first user interaction with the carousel.
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return undefined;
        const dismiss = () => setShowCue(false);
        scroller.addEventListener("touchstart", dismiss, { passive: true, once: true });
        scroller.addEventListener("scroll", dismiss, { passive: true, once: true });
        return () => {
            scroller.removeEventListener("touchstart", dismiss);
            scroller.removeEventListener("scroll", dismiss);
        };
    }, []);

    // Escape closes the currently open card.
    useEffect(() => {
        if (openIdx === null) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setOpenIdx(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [openIdx]);

    function scrollTo(index) {
        const el = slideRefs.current[index];
        if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
        }
        setShowCue(false);
    }

    function goPrev() { if (current > 0) scrollTo(current - 1); }
    function goNext() { if (current < total - 1) scrollTo(current + 1); }

    function onToggle(index) {
        setShowCue(false);
        setOpenIdx((cur) => (cur === index ? null : index));
    }

    return (
        <section
            className="section-pad relative"
            data-testid="home-event-types-mobile-section"
        >
            <div className="container-editorial">
                <div className="mb-8">
                    <SectionEyebrow index="03" label="What We Create" />
                    <Reveal>
                        <h2 className="display-2 text-white text-balance">
                            Experiences designed
                            <br />
                            to <span className="text-[#FF2A2A]">earn attention.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="body-md mt-4">
                            From brand launches and cultural takeovers to creator nights and product discovery&mdash;each format is built around a clear business and audience outcome.
                        </p>
                    </Reveal>
                </div>
            </div>

            {/* Full-bleed scroller. The section root above has horizontal
                padding; we escape it here so the peek can extend to the
                viewport edge without triggering page-level overflow. */}
            <div
                ref={scrollerRef}
                role="region"
                aria-roledescription="carousel"
                aria-label="What We Create"
                data-testid="event-types-mobile-scroller"
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 px-5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                style={{ scrollPaddingLeft: "1.25rem" }}
            >
                {EVENT_TYPES.map((et, i) => (
                    <ServiceCard
                        key={et.key}
                        et={et}
                        index={i}
                        total={total}
                        isOpen={openIdx === i}
                        onToggle={() => onToggle(i)}
                        registerRef={(el) => { slideRefs.current[i] = el; }}
                    />
                ))}
            </div>

            {/* Pagination + navigation */}
            <div className="container-editorial mt-5">
                <div
                    className="flex items-center justify-between gap-4"
                    data-testid="event-types-mobile-pagination"
                >
                    <div
                        className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/60"
                        data-testid="event-types-mobile-counter"
                        aria-live="polite"
                    >
                        <span className="text-white">{String(current + 1).padStart(2, "0")}</span>
                        <span className="mx-2 text-white/30">/</span>
                        <span>{String(total).padStart(2, "0")}</span>
                    </div>

                    <div className="flex items-center gap-2">
                        {EVENT_TYPES.map((et, i) => (
                            <button
                                key={et.key}
                                type="button"
                                aria-label={`Go to ${et.title}`}
                                aria-current={i === current ? "true" : undefined}
                                onClick={() => scrollTo(i)}
                                data-testid={`event-types-mobile-dot-${i}`}
                                className={`h-2.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${i === current ? "w-6 bg-[#FF2A2A]" : "w-2.5 bg-white/25 hover:bg-white/50"}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Prev / next — light chrome, keyboard fallback for the swipe gesture */}
                <div className="mt-4 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={goPrev}
                        disabled={current === 0}
                        aria-label="Previous service"
                        data-testid="event-types-mobile-prev"
                        className="inline-flex items-center gap-2 h-11 min-h-11 px-4 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black text-xs font-mono uppercase tracking-widest"
                    >
                        <ArrowLeft size={14} /> Prev
                    </button>

                    <AnimatePresence>
                        {showCue && (
                            <motion.div
                                key="cue"
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 4 }}
                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/45"
                                data-testid="event-types-mobile-cue"
                                aria-hidden="true"
                            >
                                Swipe to explore services
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <button
                        type="button"
                        onClick={goNext}
                        disabled={current === total - 1}
                        aria-label="Next service"
                        data-testid="event-types-mobile-next"
                        className="inline-flex items-center gap-2 h-11 min-h-11 px-4 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black text-xs font-mono uppercase tracking-widest"
                    >
                        Next <ArrowRight size={14} />
                    </button>
                </div>
            </div>
        </section>
    );
}

function ServiceCard({ et, index, total, isOpen, onToggle, registerRef }) {
    const contentId = `event-mobile-detail-${et.key}`;

    return (
        <article
            ref={registerRef}
            data-slide-index={index}
            data-testid={`event-type-mobile-card-${et.key}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${et.title} — ${index + 1} of ${total}`}
            className={`snap-start shrink-0 w-[86%] max-w-[380px] rounded-2xl border transition-colors bg-[#0B0C10] relative overflow-hidden ${isOpen ? "border-[#FF2A2A]/60" : "border-white/10"}`}
        >
            <div className="p-6 flex flex-col h-full">
                {/* Top row — tag + plus toggle */}
                <div className="flex items-start justify-between gap-3">
                    <span
                        className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/60"
                        data-testid={`event-type-mobile-tag-${et.key}`}
                    >
                        {et.tag}
                    </span>
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-expanded={isOpen}
                        aria-controls={contentId}
                        aria-label={`${isOpen ? "Close" : "Open"} ${et.title} details`}
                        data-testid={`event-type-mobile-toggle-${et.key}`}
                        className={`h-11 w-11 -mr-1 -mt-1 rounded-full border flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${isOpen ? "border-[#FF2A2A]/60 bg-[#FF2A2A] text-black" : "border-white/15 text-white/70 hover:text-white"}`}
                    >
                        {isOpen ? <X size={16} /> : <Plus size={16} />}
                    </button>
                </div>

                {/* Prominent service number for scan-ability */}
                <div className="mt-3">
                    <span
                        aria-hidden="true"
                        className={`font-display text-5xl leading-none ${isOpen ? "text-[#FF2A2A]" : "text-white/25"}`}
                    >
                        {String(index + 1).padStart(2, "0")}
                    </span>
                </div>

                {/* Title */}
                <h3 className="font-display text-2xl leading-tight text-white mt-4">
                    {et.title}
                </h3>

                {/* Three capability chips */}
                <div
                    className="flex flex-wrap gap-2 mt-4"
                    data-testid={`event-type-mobile-chips-${et.key}`}
                >
                    {et.bullets.map((b) => (
                        <span
                            key={b}
                            className="px-2.5 py-1 rounded-full border border-white/15 text-[11px] font-mono uppercase tracking-[0.16em] text-white/80"
                        >
                            {b}
                        </span>
                    ))}
                </div>

                {/* Expanded region — description + typical scope + CTA */}
                <AnimatePresence initial={false}>
                    {isOpen && (
                        <motion.div
                            key="detail"
                            id={contentId}
                            role="region"
                            aria-live="polite"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                            data-testid={`event-type-mobile-detail-${et.key}`}
                        >
                            <div className="mt-5 pt-5 border-t border-white/10 space-y-4">
                                <p className="text-sm text-white/80 leading-relaxed">
                                    {et.hook}
                                </p>

                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/50 mb-2">
                                        Typical scope
                                    </div>
                                    <ul className="space-y-1.5">
                                        {et.bullets.map((b) => (
                                            <li key={`scope-${b}`} className="flex items-start gap-2 text-sm text-white/85">
                                                <span className="h-1 w-1 mt-2 rounded-full bg-[#FF2A2A] shrink-0" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <Link
                                    to="/contact"
                                    onClick={handlePulseBriefClick}
                                    data-testid={`event-type-mobile-cta-${et.key}`}
                                    className="group inline-flex items-center gap-2 mt-1 h-11 px-5 rounded-full bg-[#FF2A2A] text-black text-sm font-semibold hover:bg-[#ff4747] transition-colors"
                                >
                                    Start a Pulse Brief
                                    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </Link>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </article>
    );
}
